import { useEffect, useState } from "react";
import { X, RefreshCw } from "lucide-react";
import { apiAdminStripeCheck, apiAdminStripeCheckApply, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const VERDICT_LABEL = {
  deactivate: "Will set this membership to INACTIVE",
  sync: "Will refresh status and renewal date (stays active)",
  skipped: "Nothing to do",
  error: "Stripe could not be checked — nothing will change",
};

function Snapshot({ label, s, testid }) {
  if (!s) return null;
  const c = s.credits || {};
  return (
    <div className="border border-neutral-900 p-3 text-xs font-mono text-neutral-300 space-y-1" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</div>
      <div>membership: <span data-testid={`${testid}-active`}>{s.active ? "active" : "inactive"}</span> · {s.status || "—"}</div>
      <div>next renewal: {s.next_renewal_at || "—"}</div>
      <div data-testid={`${testid}-credits`}>
        credits: {c.total} (monthly {c.subscription} · purchased {c.purchased} · admin {c.admin_grant})
      </div>
    </div>
  );
}

export function StripeCheckModal({ member, onClose, onApplied }) {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    apiAdminStripeCheck(member.user_id)
      .then(setPreview)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  }, [member.user_id]);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await apiAdminStripeCheckApply(member.user_id);
      setResult(r);
      toast.success(`Stripe check applied: ${r.result}`);
      onApplied();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const canApply = preview && ["deactivate", "sync"].includes(preview.verdict) && !result;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="stripe-check-modal">
      <div className="bg-neutral-950 border border-neutral-800 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">check with stripe</div>
            <div className="font-bold text-white mt-0.5">{member.email}</div>
          </div>
          <button onClick={onClose} data-testid="stripe-check-close" className="text-neutral-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {busy && !preview && <div className="text-xs font-mono text-neutral-500">checking Stripe (read-only)…</div>}
          {preview && !result && (
            <>
              <div className="text-xs font-mono text-neutral-400" data-testid="stripe-check-subscription">
                subscription: {preview.stripe_subscription_id || "—"} · stripe: {preview.stripe_status || "—"}
              </div>
              <div className="text-sm text-white" data-testid="stripe-check-verdict">{VERDICT_LABEL[preview.verdict]}</div>
              <div className="text-xs text-neutral-500" data-testid="stripe-check-reason">{preview.reason}</div>
              <Snapshot label="current" s={preview.current} testid="stripe-check-current" />
              <div className="text-[10px] font-mono text-neutral-600 leading-relaxed">
                Purchased and admin credits are never changed. Nothing is created or cancelled in Stripe.
              </div>
            </>
          )}
          {result && (
            <>
              <div className="text-sm text-white" data-testid="stripe-check-result">Result: {result.result}</div>
              <Snapshot label="before" s={result.before} testid="stripe-check-before" />
              <Snapshot label="after" s={result.after} testid="stripe-check-after" />
            </>
          )}
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} data-testid="stripe-check-cancel"
              className="flex-1 border border-neutral-800 text-neutral-300 hover:text-white font-mono text-xs uppercase tracking-[0.2em] px-3 py-2.5">
              {result ? "Close" : "Cancel"}
            </button>
            {canApply && (
              <button onClick={confirm} disabled={busy} data-testid="stripe-check-confirm"
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#00E5FF] hover:bg-[#00C8E0] text-black font-bold text-xs uppercase tracking-[0.15em] px-3 py-2.5 disabled:opacity-50">
                <RefreshCw className="w-3.5 h-3.5" /> Confirm
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
